<template>
  <div class="dialog-overlay">
    <div
      class="dialog-content"
      :style="{ width: dialog.width, height: dialog.height }"
    >
      <div class="dialog-header">
        <h3>{{ dialog.title }}</h3>
        <button class="close-btn" @click="$emit('close')">×</button>
      </div>

      <div class="dialog-body">
        <p v-if="dialog.description" class="aisea-text-14-600-black mb-8">
          {{ dialog.description }}
        </p>
        <component
          v-for="(component, _) in dialog.components"
          :is="component.type"
          :key="component.id"
          v-bind="{
            ...component.props,
            value: formData[component.id],
            id: component.id,
          }"
          @input="updateFormData"
          class="mb-8"
        />
      </div>

      <div class="dialog-footer">
        <button class="dismiss-btn" @click="handleAction('dismiss')">
          {{ dialog.dismissText }}
        </button>
        <button
          class="accept-btn"
          :disabled="!isFormValid"
          :class="{ 'accept-btn-disabled': !isFormValid }"
          @click="handleAction('accept')"
        >
          {{ dialog.acceptText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// Components
import DatePicker from "./DatePicker.vue";
import InputBox from "./InputBox.vue";
import SelectBox from "./SelectBox.vue";
import DropZone from "./DropZone.vue";

// Store
import { createNamespacedHelpers } from "vuex";
const {
  mapActions: mapActionsOfCrewDataManagement,
  mapState: mapStateOfCrewDataManagement,
  mapGetters: mapGettersOfCrewDataManagement,
  mapMutations: mapMutationsOfCrewDataManagement,
} = createNamespacedHelpers("crewDataManagement");

export default {
  name: "DialogComponent",
  props: {
    dialog: {
      type: Object,
      required: true,
    },
  },
  components: {
    InputBox,
    SelectBox,
    DatePicker,
    DropZone,
  },
  data() {
    return {
      formData: {},
    };
  },
  computed: {
    isFormValid() {
      return this.dialog.components.every(component => {
        if (component.props && component.props.required) {
          return !!this.formData[component.id];
        }
        return true;
      });
    },
  },
  created() {
    this.initializeFormDate();
  },
  methods: {
    initializeFormDate() {
      this.dialog.components.forEach(component => {
        this.$set(this.formData, component.id, null);
      });
    },
    updateFormData({ id, value }) {
      this.$set(this.formData, id, value);
    },
    handleAction(action) {
      if (action === "dismiss") {
        this.$emit("close");
      } else if (action === "accept") {
        this.$emit("accept", this.formData);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
}

@media (max-height: 730px) {
  .dialog-overlay {
    padding-top: 110px;
  }
}

.dialog-content {
  width: 90%;
  max-width: 600px;
  min-width: 300px;
  height: auto;
  max-height: 90vh;
  min-height: 200px;
  background: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: auto;
  animation: slide-up 0.1s ease-out forwards;
}

@keyframes slide-up {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.close-btn {
  font-size: 20px;
  background: none;
  border: none;
  cursor: pointer;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px;
  background-color: #f8f8f8;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 1px solid #e0e0e0;
}

.dialog-body {
  padding: 18px;
  flex-grow: 1;
}

.dialog-footer {
  padding: 18px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background-color: #fff;
  position: sticky;
  bottom: 0;
  border-top: 1px solid #e0e0e0;
}

.accept-btn {
  height: 2.5rem;
  padding: var(--Margin-m-15, 0.375rem) 1.125rem;
  border-style: none;
  border-radius: 8px;
  background-color: #039be5;
  color: #f2f2f2;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;

  &:hover {
    background-color: #039be5;
    color: #f2f2f2;
  }

  &.accept-btn-disabled {
    background-color: #039be5;
    opacity: 0.33;
    color: #f2f2f2;
    cursor: not-allowed;
  }
}

.dismiss-btn {
  height: 2.5rem;
  padding: var(--Margin-m-15, 0.375rem) 1.125rem;
  border-style: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
}
</style>